import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Checkbox,
  ListItemText,
  FormControl,
  TextField,
  Chip,
  Autocomplete,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import * as Yup from "yup";
import { useFormik } from "formik";
import "./QuoteModal.scss";
import { Close } from "@mui/icons-material";
// import { getServiceCategories } from "../../../api/become-partner";

import {
  validationSchema,
  QuoteFormValues,
  initialValues,
  enforceEmailPartLimits,
} from "./validationSchema";
import NewPhoneNumberComponent from "../new-phone-input-component/new-phone-input-component";
import PlannerSuccessModal from "../planner-success-modal/PlannerSuccessModal";
interface QuoteModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  servicesOptions: any[];
  partnerId: number;
  onSuccess?: () => void;
  requestFn: (partnerId: number, data: any) => Promise<any>;
  isPlanner?: boolean;
  plannerServiceCategories?: string[];
}

const QuoteModal: React.FC<QuoteModalProps> = ({
  open,
  onClose,
  title,
  description,
  servicesOptions = [],
  partnerId,
  onSuccess,
  requestFn,
  isPlanner,
  plannerServiceCategories = [],
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [countryCode, setCountryCode] = useState<string>("+1");

  const uniquePlannerCategories = Array.from(
    new Set(plannerServiceCategories || []),
  );
  const hasPlannerCategories = uniquePlannerCategories.length > 0;

  const formatNeedType = (type: string) => {
    if (!type) return "";
    if (type === "pre_need") return "Preneed";
    if (type === "at_need") return "Immediate Need";
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const formattedServicesOptions = servicesOptions.map((s) => {
    const needLabel = formatNeedType(s.needType);
    return needLabel ? `${s.name} (${needLabel})` : s.name;
  });

  const handleClose = () => {
    formik.resetForm();
    setCountryCode("+1");
    onClose();
  };

  const handleSubmit = async (
    values: QuoteFormValues,
    { resetForm }: { resetForm: () => void },
  ) => {
    try {
      // strip need type suffix from services if not a planner
      const cleanedServices = values.services.map((service) => {
        if (isPlanner) return service;
        const found = servicesOptions.find(
          (s) =>
            `${s.name} (${formatNeedType(s.needType)})` === service ||
            s.name === service,
        );
        return found ? found.name : service;
      });

      // prepending country code to phone
      const finalValues = {
        ...values,
        services: cleanedServices,
        phoneNumber: `${countryCode}${values.phoneNumber.startsWith("+") ? "" : ""}${values.phoneNumber}`,
      };

      const response = await requestFn(partnerId, finalValues);

      resetForm();

      // Notify parent  - open success modal
      if (onSuccess) onSuccess();

      // Close quote modal
      onClose();
    } catch (error) {
      console.error(error);
    }
  };

  const formik = useFormik<QuoteFormValues>({
    initialValues,
    validationSchema: isPlanner
      ? validationSchema.shape({
        services: hasPlannerCategories
          ? Yup.array().min(1, "At least one service category is required")
          : Yup.array().optional(),
      })
      : validationSchema,
    onSubmit: (values, formikHelpers) => handleSubmit(values, formikHelpers),
  });

  const renderLabel = (text: string, required?: boolean) => (
    <Box className="quote-modal__field-label">
      {text}
      {required && <span className="quote-modal__required">*</span>}
    </Box>
  );

  const phoneError =
    (formik.touched.phoneNumber || formik.submitCount > 0) &&
    formik.errors.phoneNumber;

  const allRequiredFilled =
    !!formik.values.firstName &&
    !!formik.values.lastName &&
    !!formik.values.email &&
    !!formik.values.phoneNumber &&
    formik.values.phoneNumber.length >= 10 &&
    (isPlanner && !hasPlannerCategories ? true : formik.values.services.length > 0);

  const isSubmitDisabled = formik.isSubmitting;

  // Function to remove a selected service via chip delete
  const handleDeleteService = (service: string) => {
    const updated = formik.values.services.filter((s) => s !== service);
    formik.setFieldValue("services", updated);
  };

  const handleDialogClose = (
    _event: object,
    reason: "backdropClick" | "escapeKeyDown",
  ) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") return;
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "rgba(0, 0, 0, 0.23)",
        },
        "& .MuiOutlinedInput-root:not(.Mui-error):hover .MuiOutlinedInput-notchedOutline":
        {
          borderColor: "rgba(0, 0, 0, 0.23)",
        },
        "& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline":
        {
          borderWidth: "1px",
          borderColor: "rgba(0, 0, 0, 0.23)",
        },
      }}
    >
      <DialogTitle className="quote-modal__header">
        <Box className="quote-modal__header-content">
          <span className="quote-modal__title">{title}</span>
          <IconButton
            className="quote-modal__close-btn"
            onClick={handleClose}
            size="large"
          >
            <CloseIcon fontSize="large" />
          </IconButton>
        </Box>
      </DialogTitle>

      {description && (
        <Box className="quote-modal__description">{description}</Box>
      )}

      <DialogContent>
        <form
          className="quote-modal__form"
          onSubmit={formik.handleSubmit}
          noValidate
          autoComplete="off"
        >
          <Box
            className="quote-modal__form-grid"
            sx={{
              "& .MuiFormHelperText-root": {
                margin: 0,
                padding: 0,
                lineHeight: "16px",
              },
            }}
          >
            {/* First Name */}
            <Box>
              {renderLabel("First Name", true)}
              <TextField
                name="firstName"
                placeholder="Enter First Name"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={!!(formik.touched.firstName && formik.errors.firstName)}
                helperText={formik.touched.firstName && formik.errors.firstName}
                size="small"
                fullWidth
                inputProps={{ maxLength: 50 }}
              />
            </Box>

            {/* Last Name */}
            <Box>
              {renderLabel("Last Name", true)}
              <TextField
                name="lastName"
                placeholder="Enter Last Name"
                value={formik.values.lastName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={!!(formik.touched.lastName && formik.errors.lastName)}
                helperText={formik.touched.lastName && formik.errors.lastName}
                size="small"
                fullWidth
                inputProps={{ maxLength: 50 }}
              />
            </Box>

            {/* Email */}
            <Box>
              {renderLabel("Email", true)}
              <TextField
                name="email"
                placeholder="Enter Email Id"
                value={formik.values.email}
                onChange={(e) =>
                  formik.setFieldValue(
                    "email",
                    enforceEmailPartLimits(e.target.value),
                  )
                }
                onBlur={formik.handleBlur}
                error={!!(formik.touched.email && formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                size="small"
                fullWidth
                inputProps={{ maxLength: 150, inputMode: "email" }}
              />
            </Box>

            {/* Phone */}
            <Box>
              {renderLabel("Phone Number", true)}
              {/* <TextField
                name="phone"
                placeholder="Enter Phone Number"
                value={formik.values.phone}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={!!(formik.touched.phone && formik.errors.phone)}
                // helperText={formik.touched.phone && formik.errors.phone}
                size="small"
                fullWidth
              /> */}
              <Box
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "4px !important",
                    height: "39px !important",
                    maxWidth: "100%",
                  },
                  "& .MuiInputBase-root": {
                    fontSize: "14px",
                    height: "40px !important",
                    padding: "10px 6px !important",
                  },
                  "& .MuiInputAdornment-root": {
                    backgroundColor: "#FFFFFF",
                    borderRadius: "6px 0 0 6px",
                  },
                  "& input": {
                    color: "#104145 !important",
                  },
                }}
              >
                <NewPhoneNumberComponent
                  value={formik.values.phoneNumber}
                  onChange={(value) =>
                    formik.setFieldValue("phoneNumber", value)
                  }
                  countryCode={countryCode}
                  onCountryChange={setCountryCode}
                  placeholder="Enter Phone Number"
                  onBlur={() => formik.setFieldTouched("phoneNumber", true)}
                />
                {phoneError && (
                  <Typography sx={{ color: "#d32f2f;", fontSize: "12px" }}>
                    {phoneError}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Select Services or Categories */}
            <Box>
              {renderLabel(isPlanner ? "Select Service Categories" : "Select Services", isPlanner ? hasPlannerCategories : true)}
              <FormControl size="small" fullWidth>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={
                    isPlanner
                      ? uniquePlannerCategories
                      : formattedServicesOptions
                  }
                  noOptionsText={isPlanner && !hasPlannerCategories ? "no service categories" : "No options"}
                  value={formik.values.services}
                  onChange={(_e, newValue) =>
                    formik.setFieldValue("services", newValue)
                  }
                  onBlur={(e: any) => {
                    formik.setFieldTouched("services", true);
                    formik.handleBlur(e);
                  }}
                  isOptionEqualToValue={(option, value) => option === value}
                  renderTags={() => null}
                  renderOption={(props, option, { selected }) => (
                    <li {...props} style={{ justifyContent: "space-between" }}>
                      {option}
                      <Checkbox
                        checked={selected}
                        sx={{
                          "&.Mui-checked": {
                            color: "#104145",
                          },
                        }}
                      />
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder={
                        isPlanner
                          ? (hasPlannerCategories ? "Search categories to add" : "no service categories")
                          : "Search services to add"
                      }
                      error={
                        !!(formik.touched.services && formik.errors.services)
                      }
                      helperText={
                        formik.touched.services &&
                          typeof formik.errors.services === "string"
                          ? formik.errors.services
                          : ""
                      }
                    />
                  )}
                  fullWidth
                  slotProps={{
                    paper: {
                      sx: {
                        "& .MuiAutocomplete-option[aria-selected='true']": {
                          backgroundColor: "transparent",
                        },
                        "& .MuiAutocomplete-option.Mui-focused": {
                          backgroundColor: "transparent",
                        },
                        "& .MuiAutocomplete-option:hover": {
                          backgroundColor: "rgba(0,0,0,0.01)",
                        },
                      },
                    },
                  }}
                />
              </FormControl>

              {formik.values.services.length > 0 && (
                <Box className="quote-form__chip-container">
                  {formik.values.services.map((service) => (
                    <Chip
                      key={service}
                      label={service}
                      onDelete={() => handleDeleteService(service)}
                      size="small"
                      variant="outlined"
                      className="quote-form__chip"
                    />
                  ))}
                </Box>
              )}
            </Box>

            {/* Message (beside Select Services) */}
            <Box>
              {renderLabel("Message")}
              <TextField
                name="message"
                placeholder="Tell us about your wishes and planning needs"
                value={formik.values.message}
                onChange={formik.handleChange}
                multiline
                maxRows={2}
                size="small"
                fullWidth
                inputProps={{ maxLength: 200 }}
              />
            </Box>
          </Box>

          <Box className="quote-modal__actions">
            <Button
              type="submit"
              variant="outlined"
              disabled={isSubmitDisabled}
              className="quote-modal__actions-btn"
            >
              Request Quote
            </Button>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default QuoteModal;
